/**
 * CommandBus — middleware pipeline runner for all chat-side user actions.
 *
 * Middleware composition is the classic onion: `use` order is also the
 * "before" execution order; each middleware's after-`await next` code
 * runs in the reverse order. Throws propagate up the chain — that's how
 * `errorMappingMiddleware` catches every command's failure in one place.
 */

import type { MachineRegistry } from 'src/content/ChatV2/machines';
import type {
  ChatStreamRegistry,
  StreamRunner,
  StreamSettler
} from 'src/content/ChatV2/stream';
import type { ChatTransport } from 'src/content/ChatV2/transport/ChatTransport';
import type { ChatId, CorrelationId } from 'src/content/ChatV2/types/ids';
import logger from 'src/utils/logger';

import type {
  CancelRunFn,
  CommandContext,
  CommandInvocation,
  CommandMiddleware,
  CommandNext,
  ICommand,
  PostMessageFn,
  TurnEvents
} from './ICommand';

export interface CommandBusServices {
  machineRegistry: MachineRegistry;
  streamRegistry: ChatStreamRegistry;
  streamRunner: StreamRunner;
  streamSettler: StreamSettler;
  transport: ChatTransport;
}

export interface CommandBusDispatchOptions {
  chatId: ChatId;
  postMessage: PostMessageFn;
  cancelRun?: CancelRunFn;
  correlationId?: CorrelationId;
  queryClientId?: string;
  responseClientId?: string;
}

export class CommandBus {
  private readonly middlewares: CommandMiddleware[] = [];

  constructor(private readonly services: CommandBusServices) {}

  use(middleware: CommandMiddleware): this {
    this.middlewares.push(middleware);
    return this;
  }

  middlewareNames(): string[] {
    return this.middlewares.map((m) => m.name);
  }

  async dispatch<TPayload>(
    command: ICommand<TPayload>,
    payload: TPayload,
    events: TurnEvents,
    options: CommandBusDispatchOptions
  ): Promise<void> {
    const context: CommandContext = {
      ...this.services,
      chatId: options.chatId,
      correlationId: options.correlationId ?? generateCorrelationId(),
      postMessage: options.postMessage,
      cancelRun: options.cancelRun,
      queryClientId: options.queryClientId,
      responseClientId: options.responseClientId
    };

    const invocation: CommandInvocation<TPayload> = {
      command,
      payload,
      context
    };

    let next: CommandNext = () => command.execute(context, payload, events);
    for (let i = this.middlewares.length - 1; i >= 0; i--) {
      const middleware = this.middlewares[i];
      const wrappedNext = next;
      next = () => middleware.intercept(invocation, wrappedNext);
    }

    try {
      return await next();
    } catch (error) {
      try {
        command.onFailure?.(context, error);
      } catch (compensationError) {
        logger.error('[chat] command onFailure threw', {
          command: command.name,
          chatId: context.chatId,
          correlationId: context.correlationId,
          compensationError
        });
      }
      throw error;
    }
  }
}

function generateCorrelationId(): CorrelationId {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `cid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
